// packages/web/src/components/CommandPalette.tsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api.client';

/* ─── Types ────────────────────────────────────────────────────── */
interface SearchResult {
  id: string;
  label: string;
  icon: string;
  category: 'Pages' | 'Employees' | 'Quick Actions';
  path: string;
}

interface Employee {
  id: string;
  first_name?: string;
  last_name?: string;
  employee_code?: string;
  email?: string;
}

/* ─── Static data ──────────────────────────────────────────────── */
const PAGE_ROUTES: SearchResult[] = [
  { id: 'p-dashboard',             label: 'Dashboard',              icon: '🏠', category: 'Pages', path: '/dashboard' },
  { id: 'p-employees',            label: 'Employees',              icon: '👥', category: 'Pages', path: '/employees' },
  { id: 'p-golden-record',        label: 'Golden Record',          icon: '⭐', category: 'Pages', path: '/golden-record' },
  { id: 'p-employee-lifecycle',   label: 'Employee Lifecycle',     icon: '🔄', category: 'Pages', path: '/employee-lifecycle' },
  { id: 'p-onboarding',           label: 'Onboarding / Exit',      icon: '👋', category: 'Pages', path: '/onboarding-offboarding' },
  { id: 'p-attendance',           label: 'Attendance Tracker',     icon: '📅', category: 'Pages', path: '/attendance' },
  { id: 'p-attendance-muster',    label: 'Attendance Muster',      icon: '📆', category: 'Pages', path: '/attendance-muster' },
  { id: 'p-time-tracker',         label: 'Time Tracker',           icon: '⏱️', category: 'Pages', path: '/time-tracker' },
  { id: 'p-leave',                label: 'Leave Tracker',          icon: '🌴', category: 'Pages', path: '/leave' },
  { id: 'p-leave-management',     label: 'Leave Management',       icon: '📋', category: 'Pages', path: '/leave-management' },
  { id: 'p-leave-overview',       label: 'Leave Overview',         icon: '📊', category: 'Pages', path: '/leave-overview' },
  { id: 'p-holidays',             label: 'Holiday List',           icon: '🎉', category: 'Pages', path: '/holidays' },
  { id: 'p-payroll',              label: 'Payroll Processing',     icon: '💰', category: 'Pages', path: '/payroll' },
  { id: 'p-payroll-management',   label: 'Payroll Management',     icon: '💵', category: 'Pages', path: '/payroll-management' },
  { id: 'p-claims',               label: 'Expense Claims',         icon: '🧾', category: 'Pages', path: '/claims' },
  { id: 'p-loans',                label: 'Employee Loans',         icon: '🏧', category: 'Pages', path: '/loans' },
  { id: 'p-settlement',           label: 'F&F Settlement',         icon: '📦', category: 'Pages', path: '/settlement' },
  { id: 'p-salary-compliance',    label: 'Salary & Compliance',    icon: '🏦', category: 'Pages', path: '/salary-compliance' },
  { id: 'p-statutory',            label: 'Statutory Config',       icon: '⚖️', category: 'Pages', path: '/statutory' },
  { id: 'p-salary-revision',      label: 'Salary Revisions',      icon: '📈', category: 'Pages', path: '/salary-revision' },
  { id: 'p-fbp',                  label: 'Flexible Benefits',      icon: '🎁', category: 'Pages', path: '/fbp' },
  { id: 'p-reimbursements',       label: 'Reimbursements',         icon: '🧾', category: 'Pages', path: '/reimbursements' },
  { id: 'p-form16',               label: 'Form 16 / IT',          icon: '📝', category: 'Pages', path: '/form16' },
  { id: 'p-payslips',             label: 'My Payslips',            icon: '📄', category: 'Pages', path: '/my/payslips' },
  { id: 'p-tds',                  label: 'TDS Declaration',        icon: '📑', category: 'Pages', path: '/my/tds-declaration' },
  { id: 'p-it-declarations',      label: 'IT Declarations',        icon: '📋', category: 'Pages', path: '/it-declarations' },
  { id: 'p-service-requests',     label: 'Service Requests',       icon: '📨', category: 'Pages', path: '/service-requests' },
  { id: 'p-helpdesk',             label: 'Helpdesk',               icon: '🎧', category: 'Pages', path: '/helpdesk' },
  { id: 'p-reports',              label: 'Reports',                icon: '📊', category: 'Pages', path: '/reports' },
  { id: 'p-reports-gallery',      label: 'Reports Gallery',        icon: '📚', category: 'Pages', path: '/reports' },
  { id: 'p-analytics',            label: 'Analytics',              icon: '📉', category: 'Pages', path: '/reports-analytics' },
  { id: 'p-performance',          label: 'Performance',            icon: '🎯', category: 'Pages', path: '/performance' },
  { id: 'p-training',             label: 'Training',               icon: '🎓', category: 'Pages', path: '/training' },
  { id: 'p-lms',                  label: 'LMS / E-Learning',       icon: '📖', category: 'Pages', path: '/lms' },
  { id: 'p-surveys',              label: 'Surveys',                icon: '📊', category: 'Pages', path: '/surveys' },
  { id: 'p-assets',               label: 'Assets',                 icon: '💻', category: 'Pages', path: '/assets' },
  { id: 'p-org-settings',         label: 'Org Settings',           icon: '⚙️', category: 'Pages', path: '/org-settings' },
  { id: 'p-user-management',      label: 'User Management',        icon: '🛡️', category: 'Pages', path: '/user-management' },
  { id: 'p-audit',                label: 'Audit & Approvals',      icon: '🔍', category: 'Pages', path: '/audit-logs' },
  { id: 'p-workforce',            label: 'Workforce Mgmt',         icon: '📈', category: 'Pages', path: '/workforce-management' },
  { id: 'p-notice-board',         label: 'Notice Board',           icon: '📌', category: 'Pages', path: '/notice-board' },
  { id: 'p-engage',               label: 'Engage',                 icon: '💬', category: 'Pages', path: '/engage' },
  { id: 'p-tasks',                label: 'Tasks',                  icon: '✅', category: 'Pages', path: '/tasks' },
  { id: 'p-knowledge-base',       label: 'Knowledge Base',         icon: '📖', category: 'Pages', path: '/knowledge-base' },
  { id: 'p-masters',              label: 'Masters',                icon: '⚙️', category: 'Pages', path: '/masters' },
  { id: 'p-operations',           label: 'Operations',             icon: '🌐', category: 'Pages', path: '/operations' },
  { id: 'p-organization',         label: 'Organization',           icon: '🏢', category: 'Pages', path: '/organization' },
];

const QUICK_ACTIONS: SearchResult[] = [
  { id: 'qa-create-employee', label: 'Create Employee',  icon: '➕', category: 'Quick Actions', path: '/employees?action=create' },
  { id: 'qa-apply-leave',     label: 'Apply Leave',      icon: '🌴', category: 'Quick Actions', path: '/leave?action=apply' },
  { id: 'qa-run-payroll',     label: 'Run Payroll',      icon: '💰', category: 'Quick Actions', path: '/payroll?action=run' },
  { id: 'qa-view-payslips',   label: 'View Payslips',    icon: '📄', category: 'Quick Actions', path: '/my/payslips' },
  { id: 'qa-mark-attendance', label: 'Mark Attendance',   icon: '📅', category: 'Quick Actions', path: '/attendance?action=mark' },
  { id: 'qa-file-claim',      label: 'File Expense Claim', icon: '🧾', category: 'Quick Actions', path: '/claims?action=new' },
  { id: 'qa-service-request', label: 'Raise Service Request', icon: '📨', category: 'Quick Actions', path: '/service-requests?action=new' },
];

/* ─── Component ────────────────────────────────────────────────── */
export default function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [employeeResults, setEmployeeResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();

  /* ── Open / close with Ctrl+K / Cmd+K ── */
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  /* ── Focus input on open ── */
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      setEmployeeResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  /* ── Debounced employee search ── */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim() || query.trim().length < 2) {
      setEmployeeResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await apiClient.get<Employee[]>('/employees', {
          params: { search: query.trim() },
        });
        if (res.success && Array.isArray(res.data)) {
          setEmployeeResults(
            res.data.slice(0, 8).map(emp => ({
              id: `emp-${emp.id}`,
              label: [emp.first_name, emp.last_name].filter(Boolean).join(' ') || emp.email || emp.employee_code || 'Employee',
              icon: '👤',
              category: 'Employees' as const,
              path: `/employees/${emp.id}`,
            })),
          );
        } else {
          setEmployeeResults([]);
        }
      } catch {
        setEmployeeResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  /* ── Filter static results ── */
  const lowerQ = query.toLowerCase().trim();

  const filteredPages = useMemo(
    () => lowerQ ? PAGE_ROUTES.filter(p => p.label.toLowerCase().includes(lowerQ)).slice(0, 8) : PAGE_ROUTES.slice(0, 6),
    [lowerQ],
  );

  const filteredActions = useMemo(
    () => lowerQ ? QUICK_ACTIONS.filter(a => a.label.toLowerCase().includes(lowerQ)) : QUICK_ACTIONS,
    [lowerQ],
  );

  /* ── Merge all results ── */
  const allResults = useMemo(() => {
    const groups: { category: string; items: SearchResult[] }[] = [];
    if (filteredPages.length > 0) groups.push({ category: 'Pages', items: filteredPages });
    if (employeeResults.length > 0) groups.push({ category: 'Employees', items: employeeResults });
    if (filteredActions.length > 0) groups.push({ category: 'Quick Actions', items: filteredActions });
    return groups;
  }, [filteredPages, employeeResults, filteredActions]);

  const flatResults = useMemo(() => allResults.flatMap(g => g.items), [allResults]);

  /* ── Reset active index when results change ── */
  useEffect(() => {
    setActiveIndex(0);
  }, [flatResults.length]);

  /* ── Select a result ── */
  const selectResult = useCallback(
    (result: SearchResult) => {
      setIsOpen(false);
      navigate(result.path);
    },
    [navigate],
  );

  /* ── Keyboard navigation ── */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => (i + 1) % Math.max(flatResults.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => (i - 1 + flatResults.length) % Math.max(flatResults.length, 1));
      } else if (e.key === 'Enter' && flatResults[activeIndex]) {
        e.preventDefault();
        selectResult(flatResults[activeIndex]);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [flatResults, activeIndex, selectResult],
  );

  /* ── Scroll active item into view ── */
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!isOpen) return null;

  /* ── Render ──────────────────────────────────────────────────── */
  let flatIdx = -1;

  return (
    <div
      onClick={() => setIsOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 580,
          background: '#1e293b',
          borderRadius: 16,
          border: '1px solid rgba(148,163,184,0.15)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '60vh',
        }}
      >
        {/* ── Search input ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0.875rem 1rem',
            borderBottom: '1px solid rgba(148,163,184,0.12)',
          }}
        >
          <span style={{ fontSize: '1.1rem', opacity: 0.6 }}>🔍</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, employees, actions..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: '0.95rem',
              color: '#f1f5f9',
              fontFamily: 'inherit',
            }}
          />
          <kbd
            style={{
              fontSize: '0.65rem',
              color: '#64748b',
              background: 'rgba(148,163,184,0.1)',
              padding: '2px 8px',
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.15)',
              fontFamily: 'inherit',
            }}
          >
            ESC
          </kbd>
        </div>

        {/* ── Results ── */}
        <div
          ref={listRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0.5rem 0',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(148,163,184,0.2) transparent',
          }}
        >
          {allResults.length === 0 && !searching && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
              {query.trim() ? 'No results found' : 'Start typing to search…'}
            </div>
          )}

          {searching && employeeResults.length === 0 && filteredPages.length === 0 && filteredActions.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
              Searching…
            </div>
          )}

          {allResults.map(group => (
            <div key={group.category}>
              {/* Category header */}
              <div
                style={{
                  padding: '0.375rem 1rem 0.25rem',
                  fontSize: '0.675rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {group.category}
              </div>
              {group.items.map(item => {
                flatIdx++;
                const isActive = flatIdx === activeIndex;
                const idx = flatIdx; // capture for click handler
                return (
                  <button
                    key={item.id}
                    data-active={isActive}
                    onClick={() => selectResult(item)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      padding: '0.5rem 1rem',
                      background: isActive ? 'rgba(99,102,241,0.15)' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: isActive ? '#e0e7ff' : '#cbd5e1',
                      fontSize: '0.8375rem',
                      fontWeight: isActive ? 500 : 400,
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      transition: 'background 0.1s',
                    }}
                  >
                    <span style={{ fontSize: '1rem', width: 24, textAlign: 'center', flexShrink: 0 }}>
                      {item.icon}
                    </span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.label}
                    </span>
                    {isActive && (
                      <span style={{ fontSize: '0.65rem', color: '#6366f1', fontWeight: 600 }}>
                        ↵ Enter
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* ── Footer hint ── */}
        <div
          style={{
            padding: '0.5rem 1rem',
            borderTop: '1px solid rgba(148,163,184,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: '0.675rem',
            color: '#475569',
          }}
        >
          <span>
            <kbd style={{ background: 'rgba(148,163,184,0.1)', padding: '1px 5px', borderRadius: 3, border: '1px solid rgba(148,163,184,0.12)', fontSize: '0.65rem' }}>↑↓</kbd>
            {' '}Navigate
          </span>
          <span>
            <kbd style={{ background: 'rgba(148,163,184,0.1)', padding: '1px 5px', borderRadius: 3, border: '1px solid rgba(148,163,184,0.12)', fontSize: '0.65rem' }}>↵</kbd>
            {' '}Select
          </span>
          <span>
            <kbd style={{ background: 'rgba(148,163,184,0.1)', padding: '1px 5px', borderRadius: 3, border: '1px solid rgba(148,163,184,0.12)', fontSize: '0.65rem' }}>Esc</kbd>
            {' '}Close
          </span>
        </div>
      </div>
    </div>
  );
}
